import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import AssistantMessage from './AssistantMessage';
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILE_BYTES,
  MAX_MESSAGE_CHARS,
} from './attachments';
import {
  getAssistantMessages,
  getAssistantIsLoading,
  getAssistantIsLoadingHistory,
  getAssistantPendingNavigation,
} from './store/selectors';
import {
  sendAssistantMessage,
  setAssistantOpen,
  loadAssistantMessages,
  clearAssistantChat,
  sendAssistantFileMessage,
  buildAssistantContext,
} from './store/actions';
import Button, {
  BUTTON_TYPES,
  BUTTON_COLORS,
} from 'components/shared/button/Button';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import SearchableDropDownPaginated from 'components/shared/dropdown/SearchableDropDownPaginated';
import { getCurrentProject } from 'components/admin/projects/details/store/selectors';
import { projectsApi } from 'services/api/projects-api';
import { getDarkMode } from 'selectors/theme';
import closeSvg from 'components/shared/modal/close.svg';
import closeDarkSvg from 'components/shared/modal/close-dark.svg';

const PROJECT_PAGE_SIZE = 25;

// Projects the signed-in user can access, matching the typed name (the API only
// returns projects the user's role may read).
export const loadProjectOptions = async (input, page) => {
  const response = await projectsApi.getProjects({ by_name: input, page });
  if (!response.ok) return { options: [], hasMore: false };
  const { content, total, pageSize } = response.projects;
  const size = pageSize || PROJECT_PAGE_SIZE;
  return {
    options: (content || []).map((project) => ({
      value: project.id,
      label: project.name,
    })),
    hasMore: page * size < (total || 0),
  };
};

export function AssistantPanel({
  messages,
  isLoading,
  isLoadingHistory,
  pendingNavigation,
  darkMode,
  currentProject,
  sendMessage,
  sendFileMessage,
  onClose,
  onLoadMessages,
  onClearChat,
}) {
  const [inputValue, setInputValue] = useState('');
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [file, setFile] = useState(null);
  const [attachError, setAttachError] = useState(null);
  const [pickedProject, setPickedProject] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const messagesEndRef = useRef(null);
  const dropzoneRef = useRef(null);
  const lastAttemptRef = useRef(null);

  // On a project page the project is the one in the URL; elsewhere the user picks one.
  const urlProjectId = buildAssistantContext().project_id;
  const projectId =
    urlProjectId || (pickedProject && pickedProject.value) || null;
  const projectName = urlProjectId
    ? (currentProject &&
        currentProject.id === urlProjectId &&
        currentProject.name) ||
      ''
    : (pickedProject && pickedProject.label) || '';
  const needsProject = Boolean(file) && !projectId;

  useEffect(() => {
    onLoadMessages();
  }, []);

  useEffect(() => {
    const el = messagesEndRef.current;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, canRetry]);

  const handleClearConfirm = (confirmed) => {
    if (confirmed) {
      onClearChat();
    }
    setShowClearConfirmation(false);
  };

  const runFileAttempt = async (attempt) => {
    lastAttemptRef.current = attempt;
    setCanRetry(false);
    const result = await sendFileMessage(attempt);
    setCanRetry(!result.ok && result.retryable);
  };

  const handleSubmit = () => {
    if (isLoading) return;
    const trimmed = (inputValue || '').trim();

    if (file) {
      if (!projectId) return;
      setFile(null);
      setInputValue('');
      setAttachError(null);
      runFileAttempt({ file, content: trimmed, projectId, projectName });
      return;
    }

    if (!trimmed) return;
    sendMessage(trimmed);
    setInputValue('');
  };

  const handleRetry = () => {
    if (lastAttemptRef.current && !isLoading)
      runFileAttempt(lastAttemptRef.current);
  };

  const handleDropAccepted = (files) => {
    if (!files || files.length === 0) return;
    setFile(files[0]);
    setAttachError(null);
  };

  const handleDropRejected = (files) => {
    const rejected = files && files[0];
    setAttachError(
      rejected && rejected.size > MAX_FILE_BYTES
        ? 'That file is larger than 15 MB.'
        : 'I can read PNG, JPEG, WebP, GIF or PDF files.'
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dropzone
      ref={dropzoneRef}
      className={`assistant-panel ${darkMode ? 'dark-mode' : ''}`}
      activeClassName="assistant-panel-drop-active"
      accept={ACCEPTED_FILE_TYPES}
      maxSize={MAX_FILE_BYTES}
      multiple={false}
      disableClick
      onDropAccepted={handleDropAccepted}
      onDropRejected={handleDropRejected}
    >
      <div className="assistant-panel-header">
        <span className="assistant-panel-title">Assistant</span>
        <div className="assistant-panel-actions">
          {messages.length > 0 && (
            <button
              type="button"
              className="assistant-panel-clear"
              onClick={() => setShowClearConfirmation(true)}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="assistant-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            <img src={darkMode ? closeDarkSvg : closeSvg} alt="" />
          </button>
        </div>
      </div>

      <div className="assistant-panel-messages">
        {isLoadingHistory ? (
          <div className="assistant-panel-empty">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="assistant-panel-empty">
            Type a message to get started. I can help you navigate the 360Plus
            App, and I can also answer questions regarding the different
            document types we have (submittals, RFAs, RFIs, etc). You can also
            attach a screenshot, photo or PDF (or drop it here) and I will draft
            an RFA from it.
          </div>
        ) : (
          messages.map((msg) => <AssistantMessage key={msg.id} message={msg} />)
        )}
        {isLoading && (
          <div className="assistant-panel-loading">
            <span className="assistant-typing-dot" />
            <span className="assistant-typing-dot" />
            <span className="assistant-typing-dot" />
          </div>
        )}
        {canRetry && !isLoading && (
          <button
            type="button"
            className="assistant-panel-retry"
            onClick={handleRetry}
          >
            Try again
          </button>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="assistant-panel-input-area">
        {file && (
          <div className="assistant-attachment">
            <span className="assistant-attachment-name">
              <span role="img" aria-label="Attachment">
                📎
              </span>{' '}
              {file.name}
            </span>
            <button
              type="button"
              className="assistant-attachment-remove"
              onClick={() => setFile(null)}
              aria-label="Remove attachment"
            >
              ×
            </button>
          </div>
        )}
        {needsProject && (
          <div className="assistant-project-picker">
            <span className="assistant-project-picker-label">
              Which project is this for?
            </span>
            <SearchableDropDownPaginated
              defaultArrow
              placeholder="Choose a project..."
              loadOptions={loadProjectOptions}
              value={pickedProject}
              onChange={setPickedProject}
            />
          </div>
        )}
        {file && projectId && (
          <div className="assistant-attachment-project">
            Creating a draft RFA{projectName ? ` in ${projectName}` : ''}.
          </div>
        )}
        {attachError && (
          <div className="assistant-attach-error">{attachError}</div>
        )}
        <textarea
          className={`input-area form-control assistant-input ${
            darkMode ? 'dark-mode' : ''
          }`}
          placeholder={
            pendingNavigation
              ? 'Type the project name...'
              : file
              ? 'Add a note (optional)...'
              : 'Type your message...'
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={file ? MAX_MESSAGE_CHARS : undefined}
          rows={1}
          disabled={isLoading}
        />
        {file && inputValue.length > MAX_MESSAGE_CHARS - 200 && (
          <div className="assistant-char-count">
            {inputValue.length}/{MAX_MESSAGE_CHARS}
          </div>
        )}
        <div className="assistant-panel-input-buttons">
          <button
            type="button"
            className="assistant-attach-button"
            onClick={() => dropzoneRef.current && dropzoneRef.current.open()}
            disabled={isLoading}
            aria-label="Attach a file"
            title="Attach a screenshot, photo or PDF"
          >
            <span role="img" aria-label="Paperclip">
              📎
            </span>
          </button>
          <Button
            type={BUTTON_TYPES.CONFIRMATION}
            color={BUTTON_COLORS.GREEN}
            label={file ? 'Create draft' : 'Send'}
            onClick={handleSubmit}
            disabled={(file ? !projectId : !inputValue.trim()) || isLoading}
          />
        </div>
      </div>

      <ConfirmationModal
        show={showClearConfirmation}
        message="Are you sure you want to delete your chat with the assistant?"
        onHide={handleClearConfirm}
      />
    </Dropzone>
  );
}

AssistantPanel.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  pendingNavigation: PropTypes.shape(),
  darkMode: PropTypes.bool.isRequired,
  currentProject: PropTypes.shape(),
  onClose: PropTypes.func.isRequired,
  onLoadMessages: PropTypes.func.isRequired,
  onClearChat: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  sendFileMessage: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    messages: getAssistantMessages(state),
    isLoading: getAssistantIsLoading(state),
    isLoadingHistory: getAssistantIsLoadingHistory(state),
    pendingNavigation: getAssistantPendingNavigation(state),
    darkMode: getDarkMode(state),
    currentProject: getCurrentProject(state),
  }),
  (dispatch) => ({
    sendMessage: (content) => dispatch(sendAssistantMessage(content)),
    sendFileMessage: (attempt) => dispatch(sendAssistantFileMessage(attempt)),
    onClose: () => dispatch(setAssistantOpen(false)),
    onLoadMessages: () => dispatch(loadAssistantMessages()),
    onClearChat: () => dispatch(clearAssistantChat()),
  })
)(AssistantPanel);
